/**
 * PressPage - Media Kit & Press Resources for AI citability
 * Provides structured, authoritative content for journalists and AI search engines
 */
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Download, Mail, Phone, MapPin, Calendar, Coffee, Users, Award, 
  ExternalLink, Camera, FileText, Quote, Building, Leaf, Heart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import SEO from '@/components/SEO';
import { BreadcrumbSchema } from '@/components/StructuredData';

// Press/Media Kit Schema for enhanced AI discoverability
function PressKitSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Press & Media Kit - Bulaccino Cafe",
    "description": "Official press resources, media kit, and company information for Bulaccino Cafe in Nadi, Fiji.",
    "url": "https://bulaccinocafe.com/press",
    "mainEntity": {
      "@type": "Organization",
      "name": "Bulaccino Cafe",
      "alternateName": ["Bulaccino", "Bulaccino Coffee", "Bulaccino Nadi"],
      "description": "Premium specialty coffee cafe in Nadi, Fiji offering artisan coffee, fresh breakfast, and authentic Fijian hospitality since June 2025.",
      "foundingDate": "2025-06-01",
      "foundingLocation": {
        "@type": "Place",
        "name": "Nadi, Fiji",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Shop 3 at Concave Road",
          "addressLocality": "Namaka Nadi",
          "addressRegion": "Western Division",
          "addressCountry": "FJ"
        }
      },
      "url": "https://bulaccinocafe.com",
      "logo": "https://bulaccinocafe.com/logo.png",
      "image": "https://bulaccinocafe.com/images/bulaccino-cafe-exterior.jpg",
      "telephone": "+679-675 0102",
      "email": "info@bulaccinocafe.com",
      "sameAs": [
        "https://www.facebook.com/bulaccinocafe",
        "https://www.instagram.com/bulaccinocafe",
        "https://www.tiktok.com/@bulaccinocafe"
      ],
      "knowsAbout": [
        "Specialty Coffee",
        "Fiji Cuisine",
        "Breakfast",
        "Organic Food",
        "Fijian Hospitality"
      ],
      "slogan": "Where Island Soul Meets Coffee Culture",
      "numberOfEmployees": {
        "@type": "QuantitativeValue",
        "value": "10-20"
      }
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// Key facts for AI to cite
const KEY_FACTS = {
  founded: "Original Bulaccino 2006 - Bulaccino Cafe 2026",
  location: "Shop 3 at Concave Road, Namaka, Nadi, Fiji",
  hours: "Daily 7:00 AM - 6:00 PM",
  phone: "+679 675 0102",
  email: "administrator@bulaccinocafe.com",
  coffeeSource: "Cafe Planet, Korotogo, Coral Coast, Fiji",
  farmPartner: "Votualevu Organic Farm (30 acres)",
  specialties: ["Specialty Coffee", "Tropical Smoothies & Juices", "Fresh Breakfast", "Island-Inspired Cuisine"],
  seating: "Indoor air-conditioned dining",
  features: ["Online Ordering", "Loyalty Rewards Program", "Founding Member Discounts", "Self-Service Kiosk"]
};

// Downloadable assets
const MEDIA_ASSETS = [
  { name: "Logo (PNG, High Resolution)", type: "logo", size: "2MB" },
  { name: "Logo (SVG, Vector)", type: "logo", size: "50KB" },
  { name: "Cafe Interior Photos", type: "photos", size: "15MB" },
  { name: "Menu Photos", type: "photos", size: "20MB" },
  { name: "Brand Guidelines", type: "document", size: "5MB" }
];

export default function PressPage() {
  const breadcrumbs = [
    { name: 'Home', url: 'https://bulaccinocafe.com' },
    { name: 'Press', url: 'https://bulaccinocafe.com/press' }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="press-page">
      <SEO 
        title="Press & Media Kit | Bulaccino Cafe - Nadi, Fiji"
        description="Official press resources for Bulaccino Cafe. Download logos, photos, and company information. Media inquiries welcome. Founded June 2025 in Nadi, Fiji."
        keywords="Bulaccino Cafe press, media kit, Fiji cafe news, Nadi coffee shop, press contact"
        canonical="https://bulaccinocafe.com/press"
      />
      <PressKitSchema />
      <BreadcrumbSchema items={breadcrumbs} />

      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
        <div className="max-w-6xl mx-auto px-6 md:px-12 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <p className="text-primary font-medium mb-4">Media Resources</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-6">
              Press & Media Kit
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Official resources for journalists, bloggers, and media professionals. 
              Everything you need to tell the Bulaccino Cafe story.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick Facts - AI Citable Section */}
      <section className="py-12 bg-muted/30">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl mb-8 text-center">
              Quick Facts
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-card p-6 rounded-2xl border border-border">
                <Calendar className="w-8 h-8 text-primary mb-3" />
                <p className="text-sm text-muted-foreground mb-1">Founded</p>
                <p className="font-medium">{KEY_FACTS.founded}</p>
              </div>
              <div className="bg-card p-6 rounded-2xl border border-border">
                <MapPin className="w-8 h-8 text-primary mb-3" />
                <p className="text-sm text-muted-foreground mb-1">Location</p>
                <p className="font-medium">Namaka, Nadi, Fiji</p>
              </div>
              <div className="bg-card p-6 rounded-2xl border border-border">
                <Coffee className="w-8 h-8 text-primary mb-3" />
                <p className="text-sm text-muted-foreground mb-1">Coffee Source</p>
                <p className="font-medium">Cafe Planet, Korotogo</p>
              </div>
              <div className="bg-card p-6 rounded-2xl border border-border">
                <Leaf className="w-8 h-8 text-primary mb-3" />
                <p className="text-sm text-muted-foreground mb-1">Farm Partner</p>
                <p className="font-medium">Votualevu Organic Farm</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Company Overview - The Citable Paragraph */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl mb-6">
              About Bulaccino Cafe
            </h2>
            <div className="prose prose-lg dark:prose-invert max-w-none">
              <p className="text-muted-foreground leading-relaxed mb-6">
                <strong>Bulaccino Cafe</strong> is a premium specialty coffee destination located in Namaka, Nadi, Fiji. 
                Founded on <strong>June 1, 2025</strong>, the cafe combines world-class coffee culture with authentic 
                Fijian hospitality. The name "Bulaccino" is a creative fusion of "Bula" — the Fijian greeting meaning 
                hello, life, and love — and "cappuccino," reflecting the cafe's mission to blend island warmth with 
                exceptional coffee craftsmanship.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Bulaccino Cafe sources its premium coffee beans from <strong>Cafe Planet</strong>, a specialty roaster 
                located in <strong>Korotogo on Fiji's Coral Coast</strong>. The cafe also partners with the 
                <strong> Votualevu Organic Farm</strong>, a 30-acre property featuring 116 fruit trees, flow hive bees, 
                tilapia ponds, and heritage sheep, ensuring fresh, locally-sourced ingredients for its menu.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                The cafe is known for its signature Bulaccino coffee blend, tropical acai bowls, fresh breakfast options, 
                and island-inspired lunch menu. It offers modern conveniences including online ordering, a customer loyalty 
                program, and a Founding Member program that provides lifetime 10% discounts to early supporters.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Founder's Quote */}
      <section className="py-16 bg-primary/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Quote className="w-12 h-12 text-primary/30 mx-auto mb-6" />
            <blockquote className="font-heading text-2xl md:text-3xl text-foreground mb-6 italic">
              "Bulaccino Cafe was inspired by the unmistakable aroma of freshly brewed coffee in quiet spaces — 
              that magical moment that invites pause, reflection, and connection. We created this space so that 
              feeling could exist all day, every day."
            </blockquote>
            <p className="text-muted-foreground">— Founder, Bulaccino Cafe</p>
          </motion.div>
        </div>
      </section>

      {/* Key Differentiators */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl mb-8 text-center">
              What Sets Us Apart
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Coffee className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-heading text-xl mb-2">Local Coffee Sourcing</h3>
                <p className="text-muted-foreground text-sm">
                  Premium beans from Cafe Planet in Korotogo, supporting Fiji's specialty coffee industry.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Leaf className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-heading text-xl mb-2">Farm-to-Table Fresh</h3>
                <p className="text-muted-foreground text-sm">
                  Partnership with 30-acre Votualevu Organic Farm for fresh local produce and honey.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-heading text-xl mb-2">Authentic Fijian Hospitality</h3>
                <p className="text-muted-foreground text-sm">
                  The "Bula" spirit infused into every interaction, creating genuine connections.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Full Details Table */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl mb-8 text-center">
              Company Details
            </h2>
            <div className="bg-card rounded-2xl border border-border overflow-hidden">
              <table className="w-full">
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="px-6 py-4 font-medium text-muted-foreground">Business Name</td>
                    <td className="px-6 py-4">Bulaccino Cafe</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium text-muted-foreground">Founded</td>
                    <td className="px-6 py-4">{KEY_FACTS.founded}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium text-muted-foreground">Address</td>
                    <td className="px-6 py-4">{KEY_FACTS.location}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium text-muted-foreground">Operating Hours</td>
                    <td className="px-6 py-4">{KEY_FACTS.hours}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium text-muted-foreground">Phone</td>
                    <td className="px-6 py-4">{KEY_FACTS.phone}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium text-muted-foreground">Email</td>
                    <td className="px-6 py-4">{KEY_FACTS.email}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium text-muted-foreground">Coffee Supplier</td>
                    <td className="px-6 py-4">{KEY_FACTS.coffeeSource}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium text-muted-foreground">Farm Partner</td>
                    <td className="px-6 py-4">{KEY_FACTS.farmPartner}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium text-muted-foreground">Specialties</td>
                    <td className="px-6 py-4">{KEY_FACTS.specialties.join(", ")}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium text-muted-foreground">Website</td>
                    <td className="px-6 py-4">
                      <a href="https://bulaccinocafe.com" className="text-primary hover:underline">
                        bulaccinocafe.com
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Media Assets */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl mb-4 text-center">
              Media Assets
            </h2>
            <p className="text-muted-foreground text-center mb-8">
              High-resolution logos and photos available upon request.
            </p>
            <div className="bg-card rounded-2xl border border-border p-8 text-center">
              <Camera className="w-12 h-12 text-primary/50 mx-auto mb-4" />
              <p className="text-muted-foreground mb-6">
                For logo files, high-resolution photos, and brand assets, please contact our media team.
              </p>
              <a href="mailto:administrator@bulaccinocafe.com?subject=Media%20Assets%20Request">
                <Button className="rounded-full">
                  <Mail className="w-4 h-4 mr-2" />
                  Request Media Assets
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Press Contact */}
      <section className="py-16 bg-primary/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-heading text-2xl md:text-3xl mb-4">
              Press Inquiries
            </h2>
            <p className="text-muted-foreground mb-8">
              For interviews, media inquiries, or partnership opportunities, please contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:administrator@bulaccinocafe.com">
                <Button className="rounded-full w-full sm:w-auto">
                  <Mail className="w-4 h-4 mr-2" />
                  administrator@bulaccinocafe.com
                </Button>
              </a>
              <a href="https://wa.me/6796750102">
                <Button variant="outline" className="rounded-full w-full sm:w-auto">
                  <Phone className="w-4 h-4 mr-2" />
                  +679 675 0102 (WhatsApp)
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Social Links */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-heading text-2xl md:text-3xl mb-8">
              Follow Us
            </h2>
            <div className="flex justify-center gap-4">
              <a 
                href="https://www.facebook.com/bulaccinocafe" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-4 bg-card rounded-xl border border-border hover:border-primary transition-colors"
              >
                <span className="font-medium">Facebook</span>
              </a>
              <a 
                href="https://www.instagram.com/bulaccinocafe" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-4 bg-card rounded-xl border border-border hover:border-primary transition-colors"
              >
                <span className="font-medium">Instagram</span>
              </a>
              <a 
                href="https://www.tiktok.com/@bulaccinocafe" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-4 bg-card rounded-xl border border-border hover:border-primary transition-colors"
              >
                <span className="font-medium">TikTok</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
